export const ACTIVITY_LOG_PAGE_SIZE = 10;
export const ACTIVITY_API_PAGE_SIZE = 100;
export const ACTIVITY_LOG_POLL_INTERVAL_MS = 10000;

export interface ExecutionActivityEntry {
  id: string;
  executionId?: string;
  actionType: string;
  executionType?: string;
  triggerType?: string;
  status: string;
  statusMessage?: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  runningAtUtc?: string | null;
  updatedAtUtc?: string;
  durationMs?: number | null;
  pipelineRunUrl?: string;
  errorMessage?: string;
  metadataJson?: string;
  targetResourceGroup?: string;
  targetResourceName?: string;
}

export interface ActivityFeedEntry {
  id: string;
  source: "execution" | "audit";
  actionType: string;
  status: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  runningAtUtc?: string | null;
  updatedAtUtc?: string;
  durationMs?: number | null;
  detailsJson?: string;
  statusMessage?: string;
  errorMessage?: string;
  pipelineRunUrl?: string;
  triggerType?: string;
  executionType?: string;
  targetResourceGroup?: string;
  targetResourceName?: string;
}

export function isExecutionActive(status: string): boolean {
  const normalizedStatus = String(status).toLowerCase();
  return normalizedStatus === "queued" || normalizedStatus === "running";
}

export function parseActivityDetailsJson<T = any>(detailsJson?: string): T | null {
  if (!detailsJson) {
    return null;
  }

  try {
    return JSON.parse(detailsJson) as T;
  } catch {
    return null;
  }
}

export interface ActivityChangeField {
  field: string;
  before: unknown;
  after: unknown;
}

export function hasActivityValueChanged(before: unknown, after: unknown): boolean {
  const normalize = (value: unknown) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item).trim());
    }

    if (value === undefined || value === null || value === "") {
      return "";
    }

    return String(value).trim();
  };

  return JSON.stringify(normalize(before)) !== JSON.stringify(normalize(after));
}

export function formatActivityValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (value === undefined || value === null || value === "") {
    return "-";
  }

  return String(value);
}

export function getActivityChangeFields(
  detailsJson?: string
): ActivityChangeField[] {
  const parsed = parseActivityDetailsJson<Record<string, unknown>>(detailsJson);
  if (!parsed || !Array.isArray(parsed.fields)) {
    return [];
  }

  return parsed.fields.filter((change): change is ActivityChangeField => {
    if (!change || typeof change !== "object") {
      return false;
    }

    const fieldChange = change as Partial<ActivityChangeField>;
    return typeof fieldChange.field === "string"
      && hasActivityValueChanged(fieldChange.before, fieldChange.after);
  });
}

export function formatActivityChangeLines(
  detailsJson: string | undefined,
  labels: Record<string, string> = {},
): string[] {
  return getActivityChangeFields(detailsJson).map((change) => {
    const label = labels[change.field] ?? change.field;
    return `${label}: ${formatActivityValue(change.before)} -> ${formatActivityValue(change.after)}`;
  });
}

export function formatActivitySchedule(value: unknown): string {
  const labels: Record<string, string> = {
    upscale: "Upscale",
    downscale: "Downscale",
    weekend: "Weekend",
  };
  const normalized = String(value ?? "").trim().toLowerCase();
  return labels[normalized] ?? String(value ?? "");
}

export function formatActivityScheduleChangeLines(detailsJson?: string): string[] {
  const scheduleNames = ["upscale", "downscale", "weekend"];
  const fields = getActivityChangeFields(detailsJson);

  return scheduleNames
    .filter((schedule) => fields.some((change) => change.field.toLowerCase().startsWith(schedule)))
    .map((schedule) => `Schedule: ${formatActivitySchedule(schedule)}`);
}

export function mapExecutionFeedEntries(
  executionItems: ExecutionActivityEntry[],
): ActivityFeedEntry[] {
  return executionItems.map((execution) => ({
    id: execution.executionId || execution.id,
    source: "execution",
    actionType: execution.actionType,
    status: execution.status,
    actorName: execution.actorName,
    actorEmail: execution.actorEmail,
    startedAtUtc: execution.startedAtUtc,
    runningAtUtc: execution.runningAtUtc,
    updatedAtUtc: execution.updatedAtUtc,
    durationMs: execution.durationMs,
    detailsJson: execution.metadataJson,
    statusMessage: execution.statusMessage,
    errorMessage: execution.errorMessage,
    pipelineRunUrl: execution.pipelineRunUrl,
    triggerType: execution.triggerType,
    executionType: execution.executionType,
    targetResourceGroup: execution.targetResourceGroup,
    targetResourceName: execution.targetResourceName,
  }));
}

export function sortActivityFeedEntries<T extends Pick<ActivityFeedEntry, "startedAtUtc">>(
  entries: T[],
): T[] {
  return [...entries].sort(
    (left, right) =>
      new Date(right.startedAtUtc).getTime() -
      new Date(left.startedAtUtc).getTime(),
  );
}
